import { useCallback, useState } from "react";
import { TicketBasket } from "@shared/modules/basket/types";
import { getPublicPurchasePlatform } from "@shared/modules/basket/utils/basket-utils";
import { retrieveBasketWithRetry } from "@shared/modules/basket/utils/basket-retrieval-utils";
import { useBasket } from "@shared/modules/basket/services/useBasket";
import { PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { DataProvider } from "@shared/constants/common";
import UserBasedTicketBasketRepository from "@app-contexts/user/features/basket/data/repositories/UserBasedTicketBasketRepository/UserBasedTicketBasketRepository";
import { useStore } from "@shared/store/useStore";
import { isStorageAvailable } from "@shared/utils/storage/cache-storage";
import { useBasketUpgrades } from "@shared/modules/basket/hooks/useBasketUpgrades";
import { usePublicBasketItemData } from "./usePublicBasketItemData";

/**
 * Basket hook for public baskets
 * Should only be used directly when the component is only used in the "public" app-context. If using this service
 * in a shared component, should use the `useBasketService` instead. That hooks into the basket service
 * context and allows the component to be agnostic of the basket service being used.
 */
export function usePublicBasket(
  config: TicketBasket & {
    enableItemData?: boolean;
  } = {
    purchase_source_platform: getPublicPurchasePlatform(),
    payment_type: PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
    enableItemData: false,
    // TODO take into account platform when reading from localstorage-like APIs
    // Also if performance is an issue,
    // monitor this as this is performing a synch. localStorage read across ALL usePublicBasket() calls
    resdiary_booking_reference: isStorageAvailable("localStorage")
      ? localStorage.getItem("resdiary_booking_reference") || null
      : null,
  }
) {
  const setBasket = useStore((state) => state.setBasket);
  const basketService = useBasket(DataProvider.Public, config);

  const [basketItemDataEnabled, setBasketItemDataEnabled] = useState(
    config.enableItemData ?? false
  );

  // Provides events, products, memberships mappings
  const basketItemData = usePublicBasketItemData(
    basketService.itemGroups,
    basketItemDataEnabled
  );

  /**
   * Enable basket item data loading
   * Call this when you need to access event/membership/product data
   * based on the item groups (for certain features such as basket upgrades)
   * This is so we don't fetch the item data when it's not needed
   */
  const enableBasketItemData = useCallback(() => {
    setBasketItemDataEnabled(true);
  }, []);

  /**
   * Disable basket data loading
   * Call this to stop loading basket data
   */
  const disableBasketItemData = useCallback(() => {
    setBasketItemDataEnabled(false);
  }, []);

  const basketUpgrades = useBasketUpgrades({
    itemGroups: basketService.itemGroups,
    itemTypeMappings: {
      ticketTypesMapping: basketItemData.ticketTypesMapping,
      membershipLevelsMapping: basketItemData.membershipLevelsMapping,
      productsAttributesMapping: basketItemData.productsAttributesMapping,
    },
    isEnabled: basketItemDataEnabled,
    enableDataLoading: enableBasketItemData,
    currency: basketService.currency,
  });

  /**
   * Refreshes the basket by fetching the latest data from the server
   * and updating the local store like zustand
   */
  const refresh = useCallback(
    // Set no_lock=true to avoid locking the basket when only reading data
    // This is useful when you need to fetch the latest basket state but don't plan to modify it
    async ({ no_lock = false } = {}) => {
      const basketRepo = new UserBasedTicketBasketRepository();
      const { basketId } = basketService;

      if (!basketId) {
        throw new Error("No basketId to refresh");
      }

      return retrieveBasketWithRetry(async () => {
        const basket = await basketRepo.retrieve(basketId, {
          params: {
            ...(no_lock ? { no_lock } : {}),
            // required for querying the basket on the widget w/ 3rd party cookies disabled
            uuid: basketService.basketUuid,
          },
          // Suppress toast on error since we handle lock timeout errors with retry
          showToastOnError: false,
        });
        setBasket(DataProvider.Public, basket);
        return basket;
      });
    },
    [basketService.basketId, basketService.basketUuid, setBasket]
  );

  return {
    ...basketService,
    refresh,

    // Basket item data functionality
    basketItemData: {
      // Data mappings
      eventsMapping: basketItemData.eventsMapping,
      ticketTypesMapping: basketItemData.ticketTypesMapping,
      productsMapping: basketItemData.productsMapping,
      productsAttributesMapping: basketItemData.productsAttributesMapping,
      membershipGroupsMapping: basketItemData.membershipGroupsMapping,
      membershipLevelsMapping: basketItemData.membershipLevelsMapping,

      // Loading states
      isLoading: basketItemData.isLoading,
      isError: basketItemData.isError,
      isEnabled: basketItemDataEnabled,

      // Control methods
      enableBasketItemData,
      disableBasketItemData,

      // Raw query results for advanced usage
      queries: basketItemData.queries,
    },
    basketUpgrades,
  };
}

export type UsePublicBasketType = ReturnType<typeof usePublicBasket>;
